import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus, Clock, AlertTriangle, CheckCircle2, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DeltaBadge({ value, inverted = false }) {
  if (value === 0) return <Badge variant="secondary" className="text-xs gap-1"><Minus className="h-3 w-3" />0%</Badge>;
  const isGood = inverted ? value < 0 : value > 0;
  const isUp = value > 0;
  return (
    <Badge className={`text-xs gap-1 ${isGood ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {value > 0 ? '+' : ''}{value}%
    </Badge>
  );
}

function CompareCard({ label, current, previous, delta, icon: Icon, inverted = false, suffix = '' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
          </div>
          <DeltaBadge value={delta} inverted={inverted} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">{current}{suffix}</p>
            <p className="text-xs text-muted-foreground">Mês atual</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-muted-foreground">{previous}{suffix}</p>
            <p className="text-xs text-muted-foreground">Mês anterior</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RankingTable({ title, current, previous }) {
  const currEntries = Object.entries(current || {});
  const prevMap = previous || {};
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {currEntries.slice(0, 8).map(([name, count], i) => {
            const prevCount = prevMap[name] || 0;
            const diff = count - prevCount;
            return (
              <div key={name} className="flex items-center gap-2 text-sm p-1.5 rounded border">
                <span className="w-5 text-xs text-muted-foreground font-mono">{i + 1}</span>
                <span className="flex-1 truncate">{name}</span>
                <span className="font-bold">{count}</span>
                {diff !== 0 && (
                  <span className={`text-xs ${diff > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {diff > 0 ? '+' : ''}{diff}
                  </span>
                )}
              </div>
            );
          })}
          {currEntries.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Sem dados</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function MonthlyComparisonPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/monthly-comparison`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const curr = data?.current || {};
  const prev = data?.previous || {};
  const deltas = data?.deltas || {};

  return (
    <div className="space-y-6" data-testid="monthly-comparison">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Relatório Comparativo Mensal</h2>
          <p className="text-sm text-muted-foreground">{curr.label || 'Mês atual'} vs {prev.label || 'Mês anterior'}</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-xs">{curr.label} ({curr.period_days}d)</Badge>
          <Badge variant="secondary" className="text-xs">{prev.label} ({prev.period_days}d)</Badge>
        </div>
      </div>

      {/* KPI Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CompareCard label="Incidentes/dia" current={curr.daily_average || 0} previous={prev.daily_average || 0} delta={deltas.incidents || 0} icon={AlertTriangle} inverted />
        <CompareCard label="MTTR" current={curr.mttr_minutes || 0} previous={prev.mttr_minutes || 0} delta={deltas.mttr || 0} icon={Clock} inverted suffix="min" />
        <CompareCard label="Total Incidentes" current={curr.total_incidents || 0} previous={prev.total_incidents || 0} delta={Math.round(((curr.total_incidents || 0) - (prev.total_incidents || 0)) / Math.max(prev.total_incidents || 1, 1) * 100)} icon={Activity} inverted />
        <CompareCard label="Resolvidos" current={curr.resolved || 0} previous={prev.resolved || 0} delta={deltas.resolved || 0} icon={CheckCircle2} />
      </div>

      {/* Severity Comparison */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider">Comparação por Severidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['Disaster', 'High', 'Average', 'Warning', 'Information'].map(sev => {
              const c = (curr.severity || {})[sev] || 0;
              const p = (prev.severity || {})[sev] || 0;
              const diff = c - p;
              const colors = { Disaster: 'border-red-300 bg-red-50', High: 'border-orange-300 bg-orange-50', Average: 'border-amber-300 bg-amber-50', Warning: 'border-yellow-300 bg-yellow-50', Information: 'border-cyan-300 bg-cyan-50' };
              return (
                <div key={sev} className={`border rounded-lg p-3 text-center ${colors[sev]}`}>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{sev}</p>
                  <p className="text-xl font-bold">{c}</p>
                  <p className="text-sm text-muted-foreground">anterior: {p}</p>
                  {diff !== 0 && <p className={`text-xs font-bold ${diff > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{diff > 0 ? '+' : ''}{diff}</p>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingTable title="Top Hosts (mês atual)" current={curr.top_hosts} previous={prev.top_hosts} />
        <RankingTable title="Top Triggers (mês atual)" current={curr.top_triggers} previous={prev.top_triggers} />
      </div>
    </div>
  );
}
